/**
 * Singbac Incorporation Post preset (my_preset / sg / singbac) — 자체완결 모듈.
 *
 *   my_preset/
 *     sg/
 *       singbac/                       ← 이 모듈 (Singapore, Singbac 문서셋 · 7종)
 *         incorporation-post/      문서 컴포넌트 7종 + package + cdd 플러밍 + SgPreSigners
 *         components/PreSetSingbacIncorporationPostPanel.vue
 *         mappers/mapSingbacIncorporationPostToPreValues.ts
 *         chatflows/singbacIncorporationPostFlow.ts
 *         utils/normalizeSingbacPreFormDataForWord.ts
 *
 * 배선: main.ts 에서 `import '@/features/startTask/my_preset/sg/singbac'` 하면
 *   이 모듈 로드 시 registerSingbacIncorporationPostPreset() 가 자동 실행되어
 *   chatFlowRegistry + panelRegistry 에 등록된다.
 *
 * 매칭 = My Preset(클라이언트 커스텀)에서 온, 이름에 'incorporation' **그리고** 'post' 가
 *   모두 들어간 선택 (priority 60 > Aura 의 plain 'incorporation' priority 50).
 *   → "Incorporation Post" 는 Singbac, plain "Incorporation" 은 그대로 Aura 가 처리.
 */
import { registerPanel } from '@/features/startTask/preset_task/shared/preset/panels/panelRegistry';
import { registerChatFlow } from '@/features/startTask/preset_task/shared/preset/chatflows/chatFlowRegistry';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import PreSetSingbacIncorporationPostPanel from './components/PreSetSingbacIncorporationPostPanel.vue';
import { singbacIncorporationPostFlow } from './chatflows/singbacIncorporationPostFlow';
import PreSetSingbacIncorporationPrePanel from './components/PreSetSingbacIncorporationPrePanel.vue';
import { singbacIncorporationPreFlow } from './chatflows/singbacIncorporationPreFlow';

export { default as PreSetSingbacIncorporationPostPanel } from './components/PreSetSingbacIncorporationPostPanel.vue';
export { default as SingbacIncorporationPostPackageDoc } from './incorporation-post/SingbacIncorporationPostPackageDoc.vue';
export { singbacIncorporationPostFlow } from './chatflows/singbacIncorporationPostFlow';
export {
  mapSingbacIncorporationPostToPreValues,
  type SingbacPostMapped,
} from './mappers/mapSingbacIncorporationPostToPreValues';

/** 라우팅 키 = 공통코드(CSB001 = Singbac Incorporation Post). 코드 전용 매칭. */
export const SINGBAC_INCORPORATION_POST_TASK_CODES: string[] = ['CSB001'];

/**
 * 선택이 Singbac Incorporation Post 인지 판정.
 *  - My Preset(클라이언트 커스텀)에서 왔고, 카테고리/업무 이름에 'incorporation' AND 'post' 가
 *    모두 들어가면 Singbac 전용 패널 사용.
 *  - 또는 지정된 task code 와 일치할 때.
 */
export function matchesSingbacIncorporationPost(sel: PreSetSelection): boolean {
  // 코드 전용 매칭(CSB001). 이름 fallback 제거 — 고객사 접두어 코드가 유일 라우팅 키.
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  return SINGBAC_INCORPORATION_POST_TASK_CODES.map((c) => c.toUpperCase()).includes(code);
}

/** 이 프리셋을 chatflow + panel 레지스트리에 등록 (import 시 자동 실행). */
export function registerSingbacIncorporationPostPreset(): void {
  registerChatFlow(singbacIncorporationPostFlow);
  registerPanel({
    id: 'singbac-incorporation-post',
    // Aura(plain 'incorporation', priority 50)보다 먼저 잡히도록 더 높게.
    priority: 60,
    match: (sel) => matchesSingbacIncorporationPost(sel),
    component: PreSetSingbacIncorporationPostPanel,
    meta: { docType: 'SINGBAC_INCORPORATION_POST', studio: true },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Singbac — Incorporation Pre (자체완결 · 별개 preset · 3종 문서)
//  incorporation-pre/ 폴더(문서·cdd 배관 자체 보유) + PreSetSingbacIncorporationPrePanel.
// ─────────────────────────────────────────────────────────────────────────────
export { default as PreSetSingbacIncorporationPrePanel } from './components/PreSetSingbacIncorporationPrePanel.vue';
export { default as SingbacIncorporationPrePackageDoc } from './incorporation-pre/SingbacIncorporationPrePackageDoc.vue';
export { singbacIncorporationPreFlow } from './chatflows/singbacIncorporationPreFlow';

/** PRE 전용 task code — 공통코드(Config Code)로 CSB002 를 등록했다(POST=CSB001 과 충돌 방지). */
export const SINGBAC_INCORPORATION_PRE_TASK_CODES: string[] = ['CSB002'];

/**
 * 선택이 Singbac Incorporation Pre 인지 판정.
 *  - 지정 task code(CSB002) 와 일치하거나,
 *  - My Preset(클라이언트 커스텀)에서 왔고 이름에 'incorporation' AND 'pre' 가 모두 들어가면
 *    Singbac Pre 전용 패널 사용. ('post' 는 위 Post 매칭이 잡으므로 서로 배타.)
 */
export function matchesSingbacIncorporationPre(sel: PreSetSelection): boolean {
  // 코드 전용 매칭(CSB002). 이름 fallback 제거 — 고객사 접두어 코드가 유일 라우팅 키.
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  return SINGBAC_INCORPORATION_PRE_TASK_CODES.map((c) => c.toUpperCase()).includes(code);
}

/** Pre 프리셋을 chatflow + panel 레지스트리에 등록 (import 시 자동 실행). */
export function registerSingbacIncorporationPrePreset(): void {
  registerChatFlow(singbacIncorporationPreFlow);
  registerPanel({
    id: 'singbac-incorporation-pre',
    priority: 62, // Post(60)·Aura(50) 보다 먼저 잡히도록(배타 매칭이지만 안전하게 우선).
    match: (sel) => matchesSingbacIncorporationPre(sel),
    component: PreSetSingbacIncorporationPrePanel,
    meta: { docType: 'SINGBAC_INCORPORATION_PRE', studio: true },
  });
}

// 모듈 import 시 자동 등록 (main.ts 의 `import '@/features/startTask/my_preset/sg/singbac'`)
registerSingbacIncorporationPostPreset();
registerSingbacIncorporationPrePreset();
